import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLongRightIcon } from '@heroicons/react/24/outline';
import BookCall from '../components/common/BookCall';
import AlignmentReadout from '../components/common/AlignmentReadout';
import { Section, SectionHeading } from '../components/common/Section';

const TENSIONS = [
  {
    n: '01',
    title: 'Will the next generation be ready?',
    body: 'Not whether they inherit — whether they can carry it with judgement. Most families assume the answer rather than check it.',
  },
  {
    n: '02',
    title: 'What is not being said?',
    body: 'Quiet disagreement about money, roles and fairness rarely surfaces at the table. It surfaces later, at the worst time.',
  },
  {
    n: '03',
    title: 'Does the wealth still reflect the family?',
    body: 'The balance sheet is easy to measure. Whether it still serves the family’s values is the harder question.',
  },
];

const STEPS = [
  {
    n: '01',
    title: 'Connect',
    body: 'A confidential conversation about where the family is today. No obligation to continue.',
  },
  {
    n: '02',
    title: 'Assess',
    body: 'Each member completes the same assessment privately, at their own pace.',
  },
  {
    n: '03',
    title: 'See clearly',
    body: 'A single readout shows where the family is aligned, and where it is not.',
  },
  {
    n: '04',
    title: 'Move forward',
    body: 'A guided plan makes the difficult conversations productive.',
  },
];

const ASSURANCES = [
  {
    k: 'Confidential',
    v: 'Individual answers are never shared with other family members. Only the aggregate informs the work.',
  },
  {
    k: 'Unhurried',
    v: 'Fifteen to twenty minutes, completed privately whenever it suits. Progress saves itself.',
  },
  {
    k: 'Equal weight',
    v: 'The founder and the youngest adult voice count the same. Those who married in are full participants.',
  },
];

const CREDENTIALS = [
  'Certificate in Family Wealth Advisory — Family Firm Institute (USA)',
  'Certificate in Family Business Advisory — Family Firm Institute (USA)',
  'Honours, Industrial Psychology — University of the Witwatersrand',
  'Seventeen years within a family business',
];

const HomePage = () => {
  return (
    <div className="bg-cream">
      {/* Hero — type on ivory, image kept separate */}
      <section>
        <div className="max-w-6xl mx-auto px-6 sm:px-10 lg:px-12 pt-20 pb-12 lg:pt-28 lg:pb-16">
          <h1 className="font-serif font-normal text-secondary-900 tracking-tight leading-[1.06]">
            <span className="block text-[2.25rem] sm:text-5xl lg:text-[3.75rem]">
              The greatest thing you’ll pass on
            </span>
            <span className="mt-4 sm:mt-6 flex sm:justify-end">
              <span className="inline-block bg-primary-800 text-cream rounded px-6 py-3 sm:px-9 sm:py-5 text-[2.25rem] sm:text-5xl lg:text-[3.75rem]">
                isn’t the wealth.
              </span>
            </span>
          </h1>

          <p className="mt-12 text-xl sm:text-2xl text-secondary-700 leading-relaxed">
            It’s <span className="text-primary-800 font-medium">a family that knows what it stands for</span>. We’re here to help you find it.
          </p>

          <div className="mt-10">
            <BookCall variant="soft" size="sm">
              Request a conversation
            </BookCall>
          </div>
        </div>

        {/* Treated image band — imagery, never a text backdrop */}
        <figure className="relative">
          <img
            src="/Herobackground.png"
            alt="A stone bridge mirrored in still water at dawn"
            className="w-full h-[42vh] sm:h-[52vh] object-cover object-center grayscale-[0.45] contrast-[1.02] brightness-[1.02]"
          />
          <div className="absolute inset-0 bg-secondary-900/10" />
          <figcaption className="max-w-6xl mx-auto px-6 sm:px-10 lg:px-12">
            <span className="block py-5 text-center text-sm text-secondary-500 font-medium tracking-wide">
              A bridge holds because every span carries its share.
            </span>
          </figcaption>
        </figure>
      </section>

      {/* The tensions */}
      <Section divider>
        <SectionHeading
          eyebrow="Why it matters"
          title="The conversations every family means to have"
        />
        <div className="mt-16 lg:mt-20 border-t border-secondary-200">
          {TENSIONS.map((t) => (
            <div
              key={t.n}
              className="grid md:grid-cols-12 gap-6 md:gap-10 py-10 lg:py-12 border-b border-secondary-200"
            >
              <div className="md:col-span-2">
                <span className="font-serif text-2xl text-secondary-300">
                  {t.n}
                </span>
              </div>
              <h3 className="md:col-span-4 font-serif text-2xl font-normal text-secondary-900 leading-snug">
                {t.title}
              </h3>
              <p className="md:col-span-6 text-secondary-600 leading-relaxed text-lg">
                {t.body}
              </p>
            </div>
          ))}
        </div>
      </Section>

      {/* The instrument */}
      <Section divider className="bg-white">
        <div className="max-w-3xl">
          <p className="text-[11px] font-semibold text-primary-700 uppercase tracking-[0.2em] mb-6">
            How we help
          </p>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-[2.75rem] font-normal text-secondary-900 leading-[1.15] tracking-tight">
            First, we listen to everyone
          </h2>
          <p className="text-lg text-secondary-600 mt-6 leading-relaxed">
            Each person shares their perspective, privately and without
            judgement. Together it becomes one honest picture of where the
            family stands.
          </p>
        </div>
        <div className="mt-16">
          <AlignmentReadout />
          <p className="text-sm text-secondary-500 mt-6">
            An illustrative example.{' '}
            <Link
              to="/what-you-discover"
              className="text-secondary-800 border-b border-secondary-400 hover:border-secondary-900 pb-0.5 transition-colors"
            >
              How to read it
            </Link>
          </p>
        </div>
      </Section>

      {/* The process */}
      <Section divider>
        <SectionHeading
          eyebrow="The process"
          title="How we walk through it, together"
        />
        <div className="mt-16 lg:mt-20 grid sm:grid-cols-2 lg:grid-cols-4">
          {STEPS.map((s, i) => (
            <div
              key={s.n}
              className={`py-8 lg:py-0 lg:pr-10 ${
                i !== STEPS.length - 1 ? 'lg:border-r border-secondary-200' : ''
              } ${i !== 0 ? 'lg:pl-10' : ''} border-b sm:border-b-0 border-secondary-200`}
            >
              <span className="font-serif text-2xl text-secondary-300">
                {s.n}
              </span>
              <h3 className="font-serif text-xl font-normal text-secondary-900 mt-4 mb-3">
                {s.title}
              </h3>
              <p className="text-secondary-600 leading-relaxed text-[15px]">
                {s.body}
              </p>
            </div>
          ))}
        </div>
        <div className="mt-14">
          <Link
            to="/what-you-discover"
            className="group inline-flex items-center text-secondary-700 hover:text-secondary-900 transition-colors"
          >
            <span className="border-b border-secondary-400 group-hover:border-secondary-900 pb-1 transition-colors">
              See what the readout reveals
            </span>
            <ArrowLongRightIcon className="w-5 h-5 ml-3 group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
        </div>
      </Section>

      {/* Assurances — single restrained ink moment */}
      <section className="bg-secondary-900">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 lg:px-12 py-24 lg:py-32">
          <p className="text-[11px] font-semibold text-primary-300 uppercase tracking-[0.2em] mb-7">
            For every family member
          </p>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-normal text-cream leading-[1.15] tracking-tight max-w-2xl mb-16">
            Honest answers need a safe place to be given.
          </h2>
          <div className="grid md:grid-cols-3 border-t border-secondary-700">
            {ASSURANCES.map((a, i) => (
              <div
                key={a.k}
                className={`py-10 md:py-12 md:pr-10 border-b md:border-b-0 border-secondary-700 ${
                  i !== ASSURANCES.length - 1 ? 'md:border-r' : ''
                } ${i !== 0 ? 'md:pl-10' : ''}`}
              >
                <h3 className="font-serif text-xl text-cream mb-4">{a.k}</h3>
                <p className="text-secondary-400 leading-relaxed text-[15px]">
                  {a.v}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Credibility */}
      <Section>
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-7">
            <p className="text-[11px] font-semibold text-primary-700 uppercase tracking-[0.2em] mb-6">
              About Amy
            </p>
            <h2 className="font-serif text-3xl md:text-4xl font-normal text-secondary-900 leading-[1.15] tracking-tight mb-8">
              She has lived this herself
            </h2>
            <p className="text-lg text-secondary-600 leading-relaxed mb-10 max-w-xl">
              Seventeen years inside her own family’s business — paired with
              formal training in family advisory. She has sat where you are
              sitting.
            </p>
            <div className="border-t border-secondary-200">
              {CREDENTIALS.map((c) => (
                <p
                  key={c}
                  className="py-4 border-b border-secondary-200 text-secondary-700"
                >
                  {c}
                </p>
              ))}
            </div>
            {/*
              PROOF PLACEHOLDER — client outcomes / reflections drop in here
              once available. Left unrendered so nothing is fabricated.
            */}
          </div>

          <div className="lg:col-span-5">
            <figure className="text-center lg:text-left">
              <div className="inline-block rounded-full p-2 ring-1 ring-secondary-200">
                <img
                  src="/Amyphoto.png"
                  alt="Amy Jaffe — Founder of The Family Legacy Bridge"
                  className="block w-52 h-52 sm:w-60 sm:h-60 rounded-full object-cover grayscale-[0.35] contrast-[1.02]"
                />
              </div>
              <figcaption className="mt-7">
                <p className="font-serif text-2xl text-secondary-900">
                  Amy Jaffe
                </p>
                <p className="text-sm text-secondary-500 mt-1.5">
                  Founder, The Family Legacy Bridge
                </p>
              </figcaption>
            </figure>
            <blockquote className="mt-8 font-serif text-xl text-secondary-800 leading-relaxed">
              “I have been through it myself. That is what drives the commitment
              to helping families get this right — before, not after.”
            </blockquote>
            <Link
              to="/about"
              className="group inline-flex items-center mt-8 text-secondary-700 hover:text-secondary-900 transition-colors"
            >
              <span className="border-b border-secondary-400 group-hover:border-secondary-900 pb-1 transition-colors">
                Amy’s story and method
              </span>
              <ArrowLongRightIcon className="w-5 h-5 ml-3 group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
          </div>
        </div>
      </Section>

      {/* For advisors */}
      <Section divider>
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-16 items-start">
          <div className="lg:col-span-3">
            <p className="text-[11px] font-semibold text-primary-700 uppercase tracking-[0.2em]">
              For advisors
            </p>
          </div>
          <div className="lg:col-span-9 max-w-2xl">
            <h2 className="font-serif text-2xl md:text-3xl font-normal text-secondary-900 leading-snug tracking-tight mb-6">
              A rigorous complement to the financial advice you already give
            </h2>
            <p className="text-lg text-secondary-600 leading-relaxed mb-8">
              When the obstacle is no longer the structure but whether the
              family stands behind it, this does not replace your advice — it
              protects it.
            </p>
            <Link
              to="/advisors"
              className="group inline-flex items-center text-secondary-700 hover:text-secondary-900 transition-colors"
            >
              <span className="border-b border-secondary-400 group-hover:border-secondary-900 pb-1 transition-colors">
                How it works for advisors
              </span>
              <ArrowLongRightIcon className="w-5 h-5 ml-3 group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
          </div>
        </div>
      </Section>

      {/* Closing */}
      <Section divider containerClassName="max-w-3xl">
        <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-normal text-secondary-900 leading-[1.15] tracking-tight mb-8">
          It begins with a conversation.
        </h2>
        <BookCall size="lg">Request a conversation</BookCall>
      </Section>
    </div>
  );
};

export default HomePage;
